import React, { useState, useEffect } from 'react';
import { List, Button, Card, Typography, Spin, Empty, Avatar } from 'antd';
import { BellOutlined, DollarOutlined, QuestionCircleOutlined, LinkOutlined, RightOutlined } from '@ant-design/icons';
import { getNotificationsApi, markNotificationsSeenApi } from '../../api/notifications';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

function getUser() {
  try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
}

function getIconAndColor(item) {
  switch (item.type) {
    case 'dosubscribe':
    case 'subscription':
      return { icon: <DollarOutlined />, color: '#1677ff' };
    case 'help':
      return { icon: <QuestionCircleOutlined />, color: '#1677ff' };
    case 'user':
      return {
        icon: <BellOutlined />,
        color: item.action === 'selling' ? '#52c41a' : item.action === 'expense' ? '#ff4d4f' : '#1677ff',
      };
    default:
      return { icon: <BellOutlined />, color: '#1677ff' };
  }
}

export default function Notifications() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const user = getUser();
    const userId = user?.id;

    setLoading(true);
    getNotificationsApi({ lang: i18n.language, user_id: userId })
      .then((res) => {
        const raw = res.data;
        setNotifications(Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []);
        if (userId) markNotificationsSeenApi({ id: userId }).catch(() => {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [i18n.language]);

  const handleTap = (item) => {
    if (item.crop_id) {
      navigate(`/farms/${item.farm_id}/farm/${item.crop_id}/expenses`);
    } else if (item.link) {
      window.open(item.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0, color: '#1a6b2a' }}>
          <BellOutlined /> {t('notifications')}
        </Title>
      </div>

      <Card style={{ borderRadius: 10 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
        ) : notifications.length === 0 ? (
          <Empty description="No notifications" />
        ) : (
          <List
            dataSource={notifications}
            renderItem={(item) => {
              const { icon, color } = getIconAndColor(item);
              const tappable = !!(item.crop_id || item.link);
              return (
                <List.Item
                  style={{
                    padding: '12px 16px',
                    borderRadius: 6,
                    marginBottom: 4,
                    cursor: tappable ? 'pointer' : 'default',
                    transition: 'background 0.15s',
                  }}
                  onClick={tappable ? () => handleTap(item) : undefined}
                  extra={tappable && <RightOutlined style={{ color: '#bfbfbf' }} />}
                >
                  <List.Item.Meta
                    avatar={
                      item.image_url ? (
                        <Avatar src={item.image_url} size={40} />
                      ) : (
                        <Avatar icon={icon} size={40} style={{ backgroundColor: color + '20', color }} />
                      )
                    }
                    title={<Text strong>{item.title}</Text>}
                    description={
                      <div>
                        <Text type="secondary">{item.message}</Text>
                        {item.link && item.link_title && (
                          <div style={{ marginTop: 4 }}>
                            <Button
                              type="link" size="small" icon={<LinkOutlined />}
                              style={{ padding: 0, color }}
                              onClick={(e) => { e.stopPropagation(); window.open(item.link, '_blank', 'noopener,noreferrer'); }}
                            >
                              {item.link_title}
                            </Button>
                          </div>
                        )}
                        <div style={{ marginTop: 2 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(item.created_at).fromNow()}</Text>
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Card>
    </div>
  );
}
